// src/components/SocialLogin.tsx
import React, { useCallback, useState } from 'react';
import { SITE_URL, socialLoginAuthUrl } from '../../configurations/api/url';
import CustomButton from '../Button/CustomButton';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { socialLogin } from '../../redux/authentication/action';
import { SentryCapture } from '../../analytics/Sentry';
import { oauthCancelMessage, type OAuthCallbackFailure } from '../../utils/oauthCallbackMessages';

type TProvider = 'meta' | 'instagram' | 'tiktok' | 'youtube';

interface ISocialLogin {
    provider: TProvider;
    /** Override the auth popup base URL (defaults to `/api/v1/auth`). */
    authBaseUrl?: string;
    /**
     * When provided, the component skips its built-in token exchange and
     * passes the raw OAuth code to the caller instead. Use this for flows
     * that need a different exchange endpoint (e.g. brand-auth).
     */
    onCodeReceived?: (code: string) => void;
    onAuthComplete?: (success: boolean, data?: any) => void;
    customButton?: JSX.Element;
    onClick?: () => void;
}

function providerLabel(provider: TProvider): string {
    if (provider === 'meta') return 'Facebook';
    if (provider === 'instagram') return 'Instagram';
    return provider.charAt(0).toUpperCase() + provider.slice(1);
}

const SocialLogin: React.FC<ISocialLogin> = ({
    provider,
    authBaseUrl,
    onCodeReceived,
    onAuthComplete,
    customButton,
    onClick,
}) => {
    const dispatch = useDispatchPromise();
    const [m, sm] = useState(`Connect with ${providerLabel(provider)}`);
    const baseUrl = authBaseUrl ?? socialLoginAuthUrl;

    const exchange = useCallback(
        (code: string) => {
            if (!code) return;

            if (onCodeReceived) {
                sm('Completed');
                onCodeReceived(code);
                return;
            }

            dispatch(socialLogin({ code, provider }))
                .then((response: any) => {
                    onAuthComplete?.(true, response.data);
                })
                .catch((error: any) => {
                    onAuthComplete?.(false, null);
                    SentryCapture(error);
                });
        },
        [dispatch, provider, onAuthComplete, onCodeReceived],
    );

    const openPopup = () => {
        const popup = window.open(`${baseUrl}/${provider}`, `${provider} Login`, 'width=600,height=700');
        onClick && onClick();
        const handleMessage = (event: MessageEvent) => {
            const appOrigin = window.location.origin;
            const configOrigin = new URL(SITE_URL).origin;
            if (event.origin !== appOrigin && event.origin !== configOrigin) return;
            if (!event.data?.type) return;

            if (event.data.type === 'SOCIAL_AUTH_SUCCESS') {
                if (event.data.provider === provider && event.data.code) {
                    window.removeEventListener('message', handleMessage);
                    sm('Completed');
                    clearInterval(timer);
                    exchange(event.data.code);
                }
            }

            if (event.data.type === 'SOCIAL_AUTH_FAILED') {
                window.removeEventListener('message', handleMessage);
                clearInterval(timer);
                const failure = event.data as OAuthCallbackFailure & { provider?: string };
                const cancelled = Boolean(failure.cancelled);
                const message =
                    failure.message || (cancelled ? oauthCancelMessage(failure.provider || provider) : undefined);
                if (message) {
                    showToastMessage(message, cancelled ? 'info' : 'error');
                }
                onAuthComplete?.(false, failure);
            }
        };

        window.addEventListener('message', handleMessage);

        const timer = setInterval(() => {
            if (popup?.closed) {
                clearInterval(timer);
                window.removeEventListener('message', handleMessage);
                // Opener already got SOCIAL_AUTH_FAILED + toast from the callback page path.
                // Plain close without a message is treated as a quiet abort (no duplicate toast).
                onAuthComplete?.(false);
            }
        }, 500);
    };

    return customButton ? (
        React.cloneElement(customButton, { onClick: openPopup })
    ) : (
        <CustomButton onClick={openPopup} label={m} />
    );
};

export { SocialLogin };
